/**
 * `layoutTree` — a tidy top-down tree layout. Pure + dependency-free.
 *
 * Issue #759 (Phase 2). §A7 makes auto-layout **mandatory**: users never
 * hand-position nodes; a tidy-tree algorithm places everything. This is a
 * small layered Reingold–Tilford-style pass:
 *
 *   1. Post-order: leaves get the next free x-slot (left→right); a parent
 *      is centred over its children's span.
 *   2. To prevent subtrees overlapping, each subtree is laid out in its own
 *      contiguous band of x-slots — we track the running x-cursor across a
 *      left-to-right walk, so sibling subtrees never collide.
 *   3. y is purely a function of depth (uniform row height).
 *
 * That yields a tidy, non-overlapping, centred tree for the realistic
 * depths a WBS reaches — without pulling in a heavy graph-layout dep.
 *
 * Collapse: a node marked collapsed contributes a single box (its own
 * footprint) and its descendants are not laid out at all (so the canvas
 * stays tidy when subtrees fold — B8.3). The caller passes the collapsed
 * set; this module never mutates it.
 *
 * The output is a flat positioned list keyed by node id, plus the edges
 * (parent→child centre-points) the canvas draws as orthogonal connectors,
 * plus the overall content bounds (for centring + fit-to-view).
 */

import { type TreeNode, flattenTree } from "./buildTree";

/** Layout tuning — kept here so the canvas and tests share one source. */
export const LAYOUT = {
  /**
   * Default node card width, px. Used as the fallback when a node carries no
   * per-node width hint. Sized so a FOCUSED card's inline quick-actions
   * (B2.2: the `state:` swatch row + the `+ sub-task` / `@ assign` / `link →`
   * / `⋯` action row) all fit within the card without spilling past its edge
   * (#894). The tidy-tree tiling is now SIZE-AWARE (see `nodeSize` below) — a
   * node may declare a smaller/larger width and neighbours shift to make room;
   * the centring + no-overlap invariants hold (see `layout.test.ts`).
   */
  nodeWidth: 264,
  /** Default node card height, px (fallback when a node carries no hint). */
  nodeHeight: 84,
  /**
   * Horizontal gap between adjacent sibling node boxes, px. Roomier than the
   * old 28px so cards never crowd and a dependency edge has clear air to route
   * between two siblings (Problem B — "room for edges to route between them").
   */
  hGap: 56,
  /**
   * Vertical gap between a parent row and its children row, px. Roomier than
   * the old 56px so a cross-row dependency edge can bow between levels without
   * grazing the row below.
   */
  vGap: 88,
} as const;

/**
 * The compact "between / connector" node footprint (#1043, insert-on-link fix).
 * A node that the founder inserted ON a dependency link (`A ──▶ new ──▶ B`)
 * renders as a small STEP sitting on the chain, not a rigid full-size
 * tree-sibling of B (the founder's complaint). It's roughly half the default
 * width + shallower so it reads as a connector bead rather than a peer card;
 * the state chip + assignee row still fit legibly at this size. Fed to
 * `layoutTree` as a per-node size hint so neighbours flow around the smaller
 * footprint, then repositioned onto the `A→B` span (see `CONNECTOR` below).
 */
export const CONNECTOR_NODE = {
  /** Compact card width, px (~half the default 264 so it reads as a bead). */
  width: 148,
  /** Compact card height, px (shallower than the default 84). */
  height: 56,
} as const;

/**
 * A per-node size hint (px). The canvas may measure a card and pass its real
 * dimensions so the layout reserves exactly the room it needs — a smaller
 * "between" node (the future insert-on-link node) takes less horizontal slot,
 * a wider one isn't crowded. Omitted dimensions fall back to the `LAYOUT`
 * defaults, so a caller that passes nothing gets the uniform tidy tree.
 */
export interface NodeSizeHint {
  width?: number;
  height?: number;
}

/** Resolve a node's size: per-node hint first, then the `LAYOUT` default. */
function sizeOf(
  nodeId: string,
  sizes: ReadonlyMap<string, NodeSizeHint> | undefined,
): { width: number; height: number } {
  const hint = sizes?.get(nodeId);
  return {
    width: hint?.width ?? LAYOUT.nodeWidth,
    height: hint?.height ?? LAYOUT.nodeHeight,
  };
}

/** A node's resolved position (top-left corner) + its centre. */
export interface PositionedNode {
  node: TreeNode;
  x: number;
  y: number;
  width: number;
  height: number;
  /** Centre x (for connectors). */
  cx: number;
  /** Centre y. */
  cy: number;
  /** True when this node has children but they're hidden (collapsed). */
  collapsed: boolean;
  /** Count of hidden descendants (for the "▶ N tasks" chip). */
  hiddenDescendants: number;
}

/** A parent→child connector, as centre-point coordinates. */
export interface LayoutEdge {
  fromId: string;
  toId: string;
  /** Parent bottom-centre. */
  x1: number;
  y1: number;
  /** Child top-centre. */
  x2: number;
  y2: number;
}

export interface TreeLayout {
  nodes: PositionedNode[];
  edges: LayoutEdge[];
  /** Content bounding box (px), for centring + fit-to-view. */
  width: number;
  height: number;
}

const { hGap, vGap } = LAYOUT;

export interface LayoutOptions {
  /**
   * Optional per-node measured/known size hints (px). When supplied the layout
   * reserves exactly the room each card needs — variable widths shift
   * neighbours; variable heights deepen only the rows that need it. Omitted ⇒
   * the uniform `LAYOUT` defaults, so existing callers are unchanged.
   */
  sizes?: ReadonlyMap<string, NodeSizeHint>;
  /**
   * The set of "between / connector" node ids (#1043, insert-on-link fix). A
   * connector node is laid out by the tidy tree like any leaf, then a
   * deterministic POST-PASS slides it onto the midpoint of its `A→B`
   * dependency span (`A ──▶ new ──▶ B`) so it visibly reads as a small step ON
   * the chain rather than a rigid full-size sibling of B. Detection (leaf + one
   * in-dep + one out-dep) lives in `connectorNodeIds`; the caller resolves it
   * from the loaded graph and passes both the ids here AND a compact size hint
   * via `sizes`. Omitted ⇒ no repositioning, so existing callers are unchanged.
   */
  connectorIds?: ReadonlySet<string>;
  /**
   * The dependency edges (`from ──▶ to`), needed only to resolve a connector
   * node's two neighbours for the reposition post-pass. Omitted ⇒ no
   * repositioning. Shape matches the canvas's `DependencyArrowEdge` subset.
   */
  dependencies?: readonly { from_node_id: string; to_node_id: string }[];
  /**
   * Round-4 ⑥ (#1043) — the off-tree `link_block` nodes the canvas wants laid
   * out alongside the tree. They are NOT part of the tidy tree (they have no
   * parent_id, no rollup, no outline-rail row), but they ARE painted as cards
   * on the canvas at their pinned `(layoutX, layoutY)`. A link_block whose
   * coordinates are NULL gets a calm staircase fallback (top-left + a small
   * per-block offset) so a freshly-created block is visible before the lead
   * drags it. Omitted ⇒ no link_blocks in the output, byte-identical to ⑤.
   */
  linkBlocks?: readonly TreeNode[];
}

/**
 * Default fallback position for a link_block whose coordinates are still NULL
 * (round-4 ⑥, #1043). Lays unpinned blocks out in a calm vertical staircase
 * near the canvas top-left so they're visible — the lead immediately drags
 * each one to its final spot.
 */
const LINK_BLOCK_FALLBACK = {
  /** First fallback X (px, plane-coord pre-`CANVAS_PAD`). */
  startX: 0,
  /** First fallback Y. */
  startY: 0,
  /** Vertical step between successive unpinned blocks. */
  stepY: 16,
} as const;

/**
 * Detect the "between / connector" nodes in the loaded graph (#1043). A
 * connector is a node that is a tree LEAF (no `parent_id` children) AND sits on
 * a chain with EXACTLY one incoming dependency AND EXACTLY one outgoing
 * dependency. That is precisely the shape the founder's "insert task between"
 * produces (`A ──▶ new ──▶ B`, with `new` parented as a leaf), and any genuine
 * single-step on-chain connector too — such a node IS a step on a chain, so the
 * compact on-chain treatment is correct for it. Pure; the canvas calls it from
 * the already-loaded tree + dependency edges (no fetch, no backend field).
 */
export function connectorNodeIds(
  roots: readonly TreeNode[],
  dependencies: readonly { from_node_id: string; to_node_id: string }[],
): Set<string> {
  const inDegree = new Map<string, number>();
  const outDegree = new Map<string, number>();
  for (const e of dependencies) {
    outDegree.set(e.from_node_id, (outDegree.get(e.from_node_id) ?? 0) + 1);
    inDegree.set(e.to_node_id, (inDegree.get(e.to_node_id) ?? 0) + 1);
  }
  const result = new Set<string>();
  for (const node of flattenTree(roots)) {
    if (node.children.length > 0) continue; // not a leaf
    if ((inDegree.get(node.id) ?? 0) === 1 && (outDegree.get(node.id) ?? 0) === 1) {
      result.add(node.id);
    }
  }
  return result;
}

/**
 * Lay out a forest. `collapsed` is the set of node ids whose subtrees are
 * folded; their descendants are omitted from the result entirely. `options`
 * may carry per-node size hints so the tidy tree is size-aware (Problem B).
 *
 * The walk is two-pass so variable row HEIGHTS resolve before any node's `y`
 * is committed:
 *   1. `place` assigns every visible node its `cx` (size-aware horizontal
 *      packing — a left→right cursor advanced by each leaf's OWN width + gap,
 *      so a slimmer node leaves more air and a wider one isn't crowded) and
 *      records its depth + height.
 *   2. row Y is the running sum of each shallower level's TALLEST node plus the
 *      vertical gap, so a level with taller cards pushes the next level down
 *      just enough — deterministic, and recomputed on every collapse/expand.
 */
export function layoutTree(
  roots: readonly TreeNode[],
  collapsed: ReadonlySet<string> = new Set(),
  options: LayoutOptions = {},
): TreeLayout {
  const { sizes, connectorIds, dependencies, linkBlocks } = options;
  const positioned: PositionedNode[] = [];
  const edges: LayoutEdge[] = [];

  // A single left→right cursor (in PX) shared across the whole walk guarantees
  // sibling subtrees occupy disjoint x-bands. Advancing it by each leaf's own
  // width (not a fixed slot) is what makes the packing size-aware.
  let cursorX = 0;

  // The tallest node at each depth, so row Y can sum real heights (size-aware
  // vertical spacing). Filled during `place`, consumed when committing `y`.
  const maxHeightAtDepth: number[] = [];

  /**
   * Place `node` and its visible descendants. Returns the node's centre x
   * (px) so the parent can centre itself over the span of its children. `y` is
   * NOT set here (heights aren't all known yet) — `place` records depth+height
   * and the second pass resolves the row offset.
   */
  const place = (node: TreeNode): number => {
    const isCollapsed = collapsed.has(node.id) && node.children.length > 0;
    const { width, height } = sizeOf(node.id, sizes);

    let cx: number;
    if (isCollapsed || node.children.length === 0) {
      // Leaf (or folded): occupy [cursorX, cursorX + width] then advance past
      // it plus the sibling gap. Centre is the midpoint of that span.
      cx = cursorX + width / 2;
      cursorX += width + hGap;
    } else {
      // Internal: place children first, then centre over their span.
      const childCentres = node.children.map((child) => place(child));
      const first = childCentres[0] ?? 0;
      const last = childCentres[childCentres.length - 1] ?? 0;
      cx = (first + last) / 2;
      // If THIS card is wider than its children's span it would spill right
      // past the cursor into the next subtree — push the cursor out so the
      // parent's right edge clears it (keeps the no-overlap invariant for an
      // unusually wide parent over narrow children).
      cursorX = Math.max(cursorX, cx + width / 2 + hGap);
    }

    maxHeightAtDepth[node.depth] = Math.max(maxHeightAtDepth[node.depth] ?? 0, height);

    positioned.push({
      node,
      x: cx - width / 2,
      // `y`/`cy` are placeholders; the second pass below sets them once every
      // level's tallest node is known.
      y: 0,
      width,
      height,
      cx,
      cy: 0,
      collapsed: isCollapsed,
      hiddenDescendants: isCollapsed ? node.totalDescendants : 0,
    });

    if (!isCollapsed) {
      for (const child of node.children) {
        // Child centre is resolved (it was placed in the recursion above);
        // look it up from the positioned list (last write wins per id). The
        // endpoint Y is patched in the second pass with the rest.
        const childPos = positioned.find((p) => p.node.id === child.id);
        if (childPos) {
          edges.push({
            fromId: node.id,
            toId: child.id,
            x1: cx,
            y1: 0,
            x2: childPos.cx,
            y2: 0,
          });
        }
      }
    }

    return cx;
  };

  for (const root of roots) {
    place(root);
    // A roomy gap between disjoint root trees (one extra sibling gap).
    cursorX += hGap;
  }

  // Second pass: resolve each depth's row top Y as the running sum of the
  // tallest node at every shallower depth + the inter-row gap. Size-aware:
  // a level of tall cards pushes the next level down by exactly its own height.
  const rowTopAtDepth: number[] = [];
  let runningY = 0;
  for (let depth = 0; depth < maxHeightAtDepth.length; depth++) {
    rowTopAtDepth[depth] = runningY;
    runningY += (maxHeightAtDepth[depth] ?? LAYOUT.nodeHeight) + vGap;
  }
  for (const p of positioned) {
    p.y = rowTopAtDepth[p.node.depth] ?? 0;
    p.cy = p.y + p.height / 2;
  }
  for (const e of edges) {
    const from = positioned.find((p) => p.node.id === e.fromId);
    const to = positioned.find((p) => p.node.id === e.toId);
    if (from) e.y1 = from.y + from.height;
    if (to) e.y2 = to.y;
  }

  // Normalise so the content's top-left sits at (0,0).
  const initialMinX = positioned.reduce((m, p) => Math.min(m, p.x), Number.POSITIVE_INFINITY);
  const initialMinY = positioned.reduce((m, p) => Math.min(m, p.y), Number.POSITIVE_INFINITY);
  const offsetX = Number.isFinite(initialMinX) ? initialMinX : 0;
  const offsetY = Number.isFinite(initialMinY) ? initialMinY : 0;

  for (const p of positioned) {
    p.x -= offsetX;
    p.cx -= offsetX;
    p.y -= offsetY;
    p.cy -= offsetY;
  }
  for (const e of edges) {
    e.x1 -= offsetX;
    e.x2 -= offsetX;
    e.y1 -= offsetY;
    e.y2 -= offsetY;
  }

  // Connector reposition post-pass (#1043, insert-on-link fix). The tidy tree
  // placed each connector node in its default leaf/sibling slot; now slide it
  // onto the MIDPOINT of its single `A→B` dependency span so it reads as a
  // small STEP sitting on the chain (`A ──▶ new ──▶ B`), "not constrained to the
  // rigid structure" (the founder's ask). Deterministic + no-overlap: we only
  // move a connector to a point that is clear of every OTHER laid-out box,
  // nudging vertically off the A–B mid-line when the geometric midpoint would
  // collide; if no clear spot is found we leave the node in its tidy slot
  // (never overlap). Runs in final (normalised) coordinates.
  if (connectorIds && connectorIds.size > 0 && dependencies && dependencies.length > 0) {
    repositionConnectors(positioned, connectorIds, dependencies);
  }

  // Round-4 ⑥ (#1043) — pinned-position override pass. Any task node whose
  // `layoutX/layoutY` are both numbers (the lead dragged it) has its tidy-tree
  // slot OVERRIDDEN with the pinned coordinates; its `x/y/cx/cy` move to the
  // pinned spot while the tidy-tree edge from its parent still draws to the
  // same `cx/cy` (the canvas re-derives connector endpoints from the moved
  // positions in the second pass below, so the edge correctly chases the
  // moved card). A node with NULL pins keeps its tidy-tree position
  // (byte-identical to before for an unpinned tree). The override runs AFTER
  // connector reposition because a pinned connector was explicitly placed by
  // the lead — the founder's intent wins over the chain heuristic.
  applyPinnedOverrides(positioned);
  // Re-patch the edges' endpoint Y/X coords against the (possibly-moved)
  // positions so a pinned card's incoming/outgoing tree connector chases it.
  for (const e of edges) {
    const from = positioned.find((p) => p.node.id === e.fromId);
    const to = positioned.find((p) => p.node.id === e.toId);
    if (from) {
      e.x1 = from.cx;
      e.y1 = from.y + from.height;
    }
    if (to) {
      e.x2 = to.cx;
      e.y2 = to.y;
    }
  }

  // Round-4 ⑥ (#1043) — append the off-tree `link_block` nodes. They were
  // never in the tidy tree (so the connector arithmetic above ignored them);
  // we place each one at its pinned coordinate, with a calm staircase
  // fallback when both are NULL so a freshly-created block is still visible
  // before the lead drags it. They render via the same card pipeline as
  // tasks; the canvas distinguishes them by `kind === 'link_block'`.
  if (linkBlocks && linkBlocks.length > 0) {
    appendLinkBlocks(positioned, linkBlocks, sizes);
  }

  // Compute width/height OVER THE WHOLE positioned set (tree + pinned + link
  // blocks) so the canvas plane reserves enough room for every card. Pinned
  // coords may be negative if a lead dragged a card to the left of x=0 — we
  // include 0 in the min so the plane never shrinks below its tidy-tree
  // origin (the inputs to the lead's drag are SCREEN positions; a negative
  // plane coord is benign, but the canvas's plane width needs to cover it).
  const maxX = positioned.reduce((m, p) => Math.max(m, p.x + p.width), 0);
  const maxY = positioned.reduce((m, p) => Math.max(m, p.y + p.height), 0);
  const minX = positioned.reduce((m, p) => Math.min(m, p.x), 0);
  const minY = positioned.reduce((m, p) => Math.min(m, p.y), 0);
  const width = maxX - minX;
  const height = maxY - minY;

  // If a pinned card or link_block dragged us into negative plane coords,
  // shift everything right/down so the content top-left lands at (0, 0)
  // again (mirrors the post-tidy normalisation at the top of this fn).
  if (minX < 0 || minY < 0) {
    for (const p of positioned) {
      p.x -= minX;
      p.cx -= minX;
      p.y -= minY;
      p.cy -= minY;
    }
    for (const e of edges) {
      e.x1 -= minX;
      e.x2 -= minX;
      e.y1 -= minY;
      e.y2 -= minY;
    }
  }

  return { nodes: positioned, edges, width, height };
}

/**
 * Override the tidy-tree slot of every node whose `layoutX/layoutY` are both
 * set with its pinned plane coordinates (round-4 ⑥, #1043). Mutates each
 * matching `PositionedNode` in place; tidy-tree positions for unpinned nodes
 * are untouched.
 *
 * The override is intentionally direct: no collision checking, no fallback to
 * the tidy slot if the pinned spot overlaps another card. The lead is
 * explicitly hand-positioning these cards via drag-to-pin; visual overlap is
 * a consequence of that choice the founder accepts. A "Reset to auto layout"
 * action restores the tidy-tree position by clearing the layout columns.
 */
function applyPinnedOverrides(positioned: PositionedNode[]): void {
  for (const p of positioned) {
    const px = p.node.layoutX;
    const py = p.node.layoutY;
    if (px == null || py == null) continue;
    p.x = px;
    p.y = py;
    p.cx = px + p.width / 2;
    p.cy = py + p.height / 2;
  }
}

/**
 * Append each off-tree `link_block` to the positioned set at its pinned
 * coordinate (round-4 ⑥, #1043). A block whose coordinates are still NULL —
 * which happens for a freshly-created block before the lead has dragged it —
 * gets a calm staircase fallback near the canvas top-left so it's visible
 * straight away.
 *
 * link_blocks render as leaves: no children, no connector arithmetic, no
 * hidden-descendants count. They participate in the canvas's pan/zoom plane
 * but not in the tree.
 */
function appendLinkBlocks(
  positioned: PositionedNode[],
  linkBlocks: readonly TreeNode[],
  sizes: ReadonlyMap<string, NodeSizeHint> | undefined,
): void {
  let fallbackIndex = 0;
  for (const block of linkBlocks) {
    const { width, height } = sizeOf(block.id, sizes);
    const hasPin = block.layoutX != null && block.layoutY != null;
    const x = hasPin ? (block.layoutX as number) : LINK_BLOCK_FALLBACK.startX;
    const y = hasPin
      ? (block.layoutY as number)
      : LINK_BLOCK_FALLBACK.startY + fallbackIndex * (height + LINK_BLOCK_FALLBACK.stepY);
    if (!hasPin) fallbackIndex += 1;
    positioned.push({
      node: block,
      x,
      y,
      width,
      height,
      cx: x + width / 2,
      cy: y + height / 2,
      collapsed: false,
      hiddenDescendants: 0,
    });
  }
}

/** Do two top-left boxes (with the layout's per-box clearance) overlap? */
function boxesCollide(
  a: { x: number; y: number; width: number; height: number },
  b: { x: number; y: number; width: number; height: number },
  pad: number,
): boolean {
  const sepX = a.x + a.width + pad <= b.x || b.x + b.width + pad <= a.x;
  const sepY = a.y + a.height + pad <= b.y || b.y + b.height + pad <= a.y;
  return !(sepX || sepY);
}

/**
 * Slide each connector node onto the midpoint of its `A→B` dependency span
 * (#1043). Mutates `positioned` in place. Deterministic: connectors are moved
 * in node-id order; each move targets the geometric centre between A and B,
 * and — if that centre would collide with another box — nudges vertically in
 * fixed steps (down first, then up) until a clear spot is found, capped so an
 * un-placeable connector simply stays in its tidy slot (no overlap is ever
 * introduced). The connector's own pre-move box is excluded from the collision
 * set so it doesn't block itself.
 */
function repositionConnectors(
  positioned: PositionedNode[],
  connectorIds: ReadonlySet<string>,
  dependencies: readonly { from_node_id: string; to_node_id: string }[],
): void {
  const byId = new Map(positioned.map((p) => [p.node.id, p]));
  // First in-dep + first out-dep per connector (a connector has exactly one of
  // each by construction; `?? ` guards a partially-laid-out graph).
  const inFrom = new Map<string, string>();
  const outTo = new Map<string, string>();
  for (const e of dependencies) {
    if (connectorIds.has(e.to_node_id) && !inFrom.has(e.to_node_id)) {
      inFrom.set(e.to_node_id, e.from_node_id);
    }
    if (connectorIds.has(e.from_node_id) && !outTo.has(e.from_node_id)) {
      outTo.set(e.from_node_id, e.to_node_id);
    }
  }

  const STEP = LAYOUT.nodeHeight / 2 + vGap / 2; // vertical nudge per attempt
  const MAX_TRIES = 8;
  // Move connectors in a stable id order so the result is deterministic.
  const ids = [...connectorIds].sort();

  for (const id of ids) {
    const me = byId.get(id);
    const aId = inFrom.get(id);
    const bId = outTo.get(id);
    if (!me || !aId || !bId) continue;
    const a = byId.get(aId);
    const b = byId.get(bId);
    if (!a || !b) continue; // an endpoint is collapsed/absent → leave in slot

    // Target centre = midpoint of the two neighbour CENTRES, so the connector
    // visibly sits on the A→B span.
    const targetCx = (a.cx + b.cx) / 2;
    const targetCy = (a.cy + b.cy) / 2;

    // Every OTHER laid-out box is an obstacle (the connector mustn't overlap a
    // peer); its two neighbours included — it sits BETWEEN them, never on them.
    const obstacles = positioned.filter((p) => p.node.id !== id);

    // Try the exact midpoint, then nudge vertically (down, up, down, up…) until
    // the placed box is clear of every obstacle. Keeps determinism.
    let placed = false;
    for (let attempt = 0; attempt <= MAX_TRIES && !placed; attempt++) {
      const dy = attempt === 0 ? 0 : (attempt % 2 === 1 ? 1 : -1) * Math.ceil(attempt / 2) * STEP;
      const cx = targetCx;
      const cy = targetCy + dy;
      const candidate = {
        x: cx - me.width / 2,
        y: cy - me.height / 2,
        width: me.width,
        height: me.height,
      };
      const collides = obstacles.some((o) => boxesCollide(candidate, o, hGap / 2));
      if (!collides) {
        me.x = candidate.x;
        me.y = candidate.y;
        me.cx = cx;
        me.cy = cy;
        placed = true;
      }
    }
    // If no clear spot was found, the connector keeps its tidy-tree slot — we
    // never trade the no-overlap invariant for the on-chain look.
  }

  // A connector may have moved to a negative coordinate (its A sits at the
  // canvas's left/top edge); re-normalise so the content's top-left is (0,0).
  const cMinX = positioned.reduce((m, p) => Math.min(m, p.x), Number.POSITIVE_INFINITY);
  const cMinY = positioned.reduce((m, p) => Math.min(m, p.y), Number.POSITIVE_INFINITY);
  const offX = Number.isFinite(cMinX) ? Math.min(0, cMinX) : 0;
  const offY = Number.isFinite(cMinY) ? Math.min(0, cMinY) : 0;
  if (offX !== 0 || offY !== 0) {
    for (const p of positioned) {
      p.x -= offX;
      p.cx -= offX;
      p.y -= offY;
      p.cy -= offY;
    }
  }
}
